import { PermissionType } from '@/enums/PermissionType';
import { catalogAccess } from '@/domain/catalog/catalog-access';
import { buildAssistantNavTargets } from '@/domain/assistant/assistant-nav-targets';
import type { IAssistantConfigResponse } from '@/interfaces/assistant.interface';
import type { IBranch } from '@/interfaces/IBranch';
import { MemberRole } from '@/interfaces/IMember';
import type {
  ICartadigitalAdminHostToolDeps,
  IGpthelpToolGate,
} from './tool-types';
import { buildScopeKey } from './tool-scope';
import { switchedOff } from './tool-switch';
import { kindOfOp, PROPOSAL_KINDS } from './proposal-kinds';

/** The host tool that prepares the menu ops. */
export const MENU_PROPOSE_TOOL = PROPOSAL_KINDS['menu.changes'].name;

/** The plan tier from which the settings page edits branches in «Sucursales» (Pro). */
const SETTINGS_PER_BRANCH_MIN_TIER = 2;

/** The stores and host hooks the gate reads. */
export type IGpthelpToolGateSource = Pick<
  ICartadigitalAdminHostToolDeps,
  'restaurantStore' | 'authStore' | 'assistantStore' | 'restaurantNav'
>;

/** Whether the current member holds `permission` (owners and admins hold all). */
export function memberHas(
  authStore: ICartadigitalAdminHostToolDeps['authStore'],
  permission: PermissionType
): boolean {
  return authStore.permissionSet?.has?.(permission) ?? false;
}

/**
 * Whether the current member works in every branch of the store: staff,
 * owners and roles over every branch (the rule `hasRole` applies).
 */
export function worksInEveryBranch(
  authStore: ICartadigitalAdminHostToolDeps['authStore']
): boolean {
  const role = authStore.currentRole;
  return (
    !!authStore.user?.admin ||
    role?.type === MemberRole.Owner ||
    !!role?.allBranches
  );
}

/**
 * The live branches the member works in, in the store's order: every one for
 * staff, owners and roles over every branch; else the role's own. The
 * settings, the sales per branch and the club program all list these.
 */
export function accessibleBranches(
  deps: Pick<ICartadigitalAdminHostToolDeps, 'restaurantStore' | 'authStore'>
): IBranch[] {
  const live = (deps.restaurantStore.branches ?? []).filter(
    (branch) => !branch.deletedAt
  );
  if (worksInEveryBranch(deps.authStore)) return live;
  const own = new Set(
    (deps.authStore.currentRole?.branches ?? []).map((branch) => branch.id)
  );
  return live.filter((branch) => own.has(branch.id));
}

/** The support link of a store's config (https or mailto:), when it has one. */
export function supportUrlOf(
  config: IAssistantConfigResponse | null
): string | null {
  const url = config?.supportUrl;
  return url && (url.startsWith('https://') || url.startsWith('mailto:'))
    ? url
    : null;
}

/**
 * The one place the tool availability gate is computed from the stores. The
 * widget's tool list, the host context `permissions` block and the fresh
 * re-check before every tool execution all read it, so they cannot drift.
 */
export function buildGpthelpToolGate(
  source: IGpthelpToolGateSource
): IGpthelpToolGate {
  const { restaurantStore, authStore, assistantStore } = source;
  const restaurant = restaurantStore.restaurant;
  const branch = restaurantStore.selectedBranch;
  const role = authStore.currentRole;

  const has = (permission: PermissionType) => memberHas(authStore, permission);

  const effectiveTier = restaurantStore.effectiveTier ?? 0;
  const multipleMenus = !!restaurantStore.restaurantSettings?.multipleMenus;
  const access = catalogAccess({
    has,
    tier: effectiveTier,
    multipleMenus,
    allBranches: worksInEveryBranch(authStore),
  });

  const scopeKey =
    typeof restaurant?.id === 'number' && typeof branch?.id === 'number'
      ? buildScopeKey({ restaurantId: restaurant.id, branchId: branch.id })
      : null;
  // The config of another store or branch never counts, and only the panel
  // renders the proposal cards (the legacy bubble's widget cannot).
  const config =
    scopeKey && assistantStore.configKey === scopeKey && assistantStore.enabled
      ? assistantStore.config
      : null;
  // A config without the list (an API older than this bundle) knows no tool
  // switch: unknown, like no config at all, so tools that ship dark stay off.
  const assistantDisabledTools =
    config && Array.isArray(config.disabledTools)
      ? [...config.disabledTools]
      : null;

  return {
    hasRestaurantScope: !!restaurant && !!branch,
    canViewStats: has(PermissionType.StatisticsView),
    canManageCaja: has(PermissionType.CajaManage),
    canViewOrdersHistory:
      has(PermissionType.OrdersHistoric) || has(PermissionType.OrdersManage),
    canManageCatalog: access.canManage,
    canDisableCatalog: access.canToggle,
    canHideEverywhere: access.canHideEverywhere,
    canManageClub: has(PermissionType.ClubManage),
    canManageReservations: has(PermissionType.ReservationsManage),
    canTopupClub: has(PermissionType.ClubTopup),
    canViewCustomers: has(PermissionType.OrdersManage),
    canManageStore: has(PermissionType.StoreSettings),
    canManageUsers: has(PermissionType.UsersManage),
    canManageBranches: has(PermissionType.BranchSettings),
    // The settings page's own rule (`multipleBranches`): the store's branches
    // are the member's, the inactive ones count for nothing.
    settingsPerBranch:
      effectiveTier >= SETTINGS_PER_BRANCH_MIN_TIER &&
      (restaurantStore.branches ?? []).filter((entry) => entry.active).length >
        1,
    allCajas: !!role?.allCajas,
    branchUsesCajas: !!branch?.settingUseCajas,
    effectiveTier,
    multipleMenus,
    // An op whose write tool is switched off cannot be prepared, so the
    // welcome, the follow-ups and the context promise only the ops of the
    // write tools left on (plan X5, F10). An op this bundle does not know
    // has no tool either.
    assistantOps: config?.writesEnabled
      ? config.ops.filter((op) => {
          const kind = kindOfOp(op);
          return (
            kind !== null &&
            !switchedOff(PROPOSAL_KINDS[kind].name, { assistantDisabledTools })
          );
        })
      : [],
    assistantWritesEnabled: !!config?.writesEnabled,
    assistantDisabledOps: config?.disabledOps ? [...config.disabledOps] : [],
    assistantDisabledTools,
    // Writes on or off: the team is reachable either way.
    supportUrl: supportUrlOf(config),
    navTargets: restaurant
      ? buildAssistantNavTargets({
          groups: source.restaurantNav(),
          tier: effectiveTier,
          basePath: `/${restaurant.stringId}`,
          canManageCatalog: access.canManage,
        })
      : [],
  };
}

/**
 * The assistant can offer to hide or show products: the member may turn
 * them on and off, and the assistant may prepare that change here (writes
 * on, plan §9.1 step 4).
 */
export function assistantCanToggle(
  gate: Pick<IGpthelpToolGate, 'canDisableCatalog' | 'assistantOps'>
): boolean {
  return (
    gate.canDisableCatalog && gate.assistantOps.includes('setAvailability')
  );
}
